import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  overviewApi,
  portfolioApi,
  tradesApi,
  settingsApi,
  analyticsApi,
  accountsApi,
  exchangeApi,
  aiApi,
  paperTradeApi,
  notificationsApi,
  reportsApi,
  healthApi,
  logsApi,
  marketApi,
  cryptocomApi,
  riskApi,
} from '@/api/client';

// --- Overview ---
export function useOverview() {
  return useQuery({
    queryKey: ['overview'],
    queryFn: () => overviewApi.get().then((r) => r.data),
    refetchInterval: 30000,
  });
}

// --- Portfolio ---
export function usePortfolioSnapshot(accountId?: string) {
  return useQuery({
    queryKey: ['portfolio', 'snapshot', accountId],
    queryFn: () => portfolioApi.snapshot(accountId).then((r) => r.data),
    refetchInterval: 15000,
  });
}

export function useEquityCurve(accountId?: string, days?: number) {
  return useQuery({
    queryKey: ['portfolio', 'equity-curve', accountId, days],
    queryFn: () => portfolioApi.equityCurve(accountId, days).then((r) => r.data),
  });
}

export function usePositions(accountId?: string) {
  return useQuery({
    queryKey: ['portfolio', 'positions', accountId],
    queryFn: () => portfolioApi.positions(accountId).then((r) => r.data),
    refetchInterval: 15000,
  });
}

// --- Trades ---
export function useTrades(filters?: Record<string, unknown>) {
  return useQuery({
    queryKey: ['trades', filters],
    queryFn: () => tradesApi.list(filters).then((r) => r.data),
  });
}

export function useSubmitTrade() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof tradesApi.submit>[0]) => tradesApi.submit(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trades'] });
      queryClient.invalidateQueries({ queryKey: ['portfolio'] });
      queryClient.invalidateQueries({ queryKey: ['overview'] });
    },
  });
}

// --- Settings ---
export function useSettings() {
  return useQuery({
    queryKey: ['settings'],
    queryFn: () => settingsApi.list().then((r) => r.data),
  });
}

export function useUpdateSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (settings: { key: string; value: string }[]) =>
      settingsApi.update(settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
  });
}

export function useRollbackSetting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (auditLogId: string) => settingsApi.rollback(auditLogId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      queryClient.invalidateQueries({ queryKey: ['audit-logs'] });
    },
  });
}

export function useAuditLogs(params?: Record<string, unknown>) {
  return useQuery({
    queryKey: ['audit-logs', params],
    queryFn: () => settingsApi.auditLogs(params).then((r) => r.data),
  });
}

// --- Analytics ---
export function useAnalyticsSummary(accountId?: string, days?: number) {
  return useQuery({
    queryKey: ['analytics', 'summary', accountId, days],
    queryFn: () => analyticsApi.summary(accountId, days).then((r) => r.data),
  });
}

export function useDailyPnl(accountId?: string, days?: number) {
  return useQuery({
    queryKey: ['analytics', 'daily-pnl', accountId, days],
    queryFn: () => analyticsApi.dailyPnl(accountId, days).then((r) => r.data),
  });
}

export function useSymbolBreakdown(accountId?: string) {
  return useQuery({
    queryKey: ['analytics', 'symbol-breakdown', accountId],
    queryFn: () => analyticsApi.symbolBreakdown(accountId).then((r) => r.data),
  });
}

export function useStrategyBreakdown(accountId?: string) {
  return useQuery({
    queryKey: ['analytics', 'strategy-breakdown', accountId],
    queryFn: () => analyticsApi.strategyBreakdown(accountId).then((r) => r.data),
  });
}

// --- Accounts ---
export function useAccounts() {
  return useQuery({
    queryKey: ['accounts'],
    queryFn: () => accountsApi.list().then((r) => r.data),
  });
}

export function useAccount(id: string) {
  return useQuery({
    queryKey: ['accounts', id],
    queryFn: () => accountsApi.get(id).then((r) => r.data),
    enabled: !!id,
  });
}

export function useCreateAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: accountsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}

export function useUpdateAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      accountsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}

export function useDeleteAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: accountsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}

// --- Exchange ---
export function useExchanges() {
  return useQuery({
    queryKey: ['exchanges'],
    queryFn: () => exchangeApi.list().then((r) => r.data),
  });
}

export function useConnectExchange() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: exchangeApi.connect,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exchanges'] });
    },
  });
}

export function useActivateLive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: exchangeApi.activateLive,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exchanges'] });
      queryClient.invalidateQueries({ queryKey: ['overview'] });
    },
  });
}

export function useEmergencyShutdown() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: exchangeApi.activateShutdown,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exchanges'] });
      queryClient.invalidateQueries({ queryKey: ['overview'] });
    },
  });
}

export function useDeactivateShutdown() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: exchangeApi.deactivateShutdown,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exchanges'] });
      queryClient.invalidateQueries({ queryKey: ['overview'] });
    },
  });
}

export function useShutdownStatus() {
  return useQuery({
    queryKey: ['shutdown-status'],
    queryFn: () => exchangeApi.shutdownStatus().then((r) => r.data),
  });
}

// --- AI ---
export function useAIProviders() {
  return useQuery({
    queryKey: ['ai-providers'],
    queryFn: () => aiApi.providers().then((r) => r.data),
  });
}

export function useAIAnalyze() {
  return useMutation({
    mutationFn: ({ symbol, provider }: { symbol: string; provider?: string }) =>
      aiApi.analyze(symbol, provider),
  });
}

export function useAIDecisions(params?: Record<string, unknown>) {
  return useQuery({
    queryKey: ['ai-decisions', params],
    queryFn: () => aiApi.decisions(params).then((r) => r.data),
  });
}

// --- Notifications ---
export function useNotificationConfig() {
  return useQuery({
    queryKey: ['notification-config'],
    queryFn: () => notificationsApi.getConfig().then((r) => r.data),
  });
}

export function useUpdateNotificationConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: notificationsApi.updateConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-config'] });
    },
  });
}

export function useTestNotification() {
  return useMutation({
    mutationFn: notificationsApi.test,
  });
}

export function useNotificationHistory(params?: Record<string, unknown>) {
  return useQuery({
    queryKey: ['notification-history', params],
    queryFn: () => notificationsApi.history(params).then((r) => r.data),
  });
}

// --- Reports ---
export function useGenerateReport() {
  return useMutation({
    mutationFn: reportsApi.generate,
  });
}

export function useEvaluation(period: string) {
  return useQuery({
    queryKey: ['evaluation', period],
    queryFn: () => reportsApi.evaluation(period).then((r) => r.data),
    enabled: !!period,
  });
}

export function useReportHistory(params?: Record<string, unknown>) {
  return useQuery({
    queryKey: ['report-history', params],
    queryFn: () => reportsApi.history(params).then((r) => r.data),
  });
}

// --- Health ---
export function useSystemHealth() {
  return useQuery({
    queryKey: ['system-health'],
    queryFn: () => healthApi.get().then((r) => r.data),
    refetchInterval: 30000,
  });
}

// --- Logs ---
export function useLogs(params?: Record<string, unknown>) {
  return useQuery({
    queryKey: ['logs', params],
    queryFn: () => logsApi.query(params).then((r) => r.data),
  });
}

export function useUserActivity(params?: Record<string, unknown>) {
  return useQuery({
    queryKey: ['user-activity', params],
    queryFn: () => logsApi.userActivity(params).then((r) => r.data),
  });
}

// --- Paper Trade ---
export function usePaperTradeAnalyze() {
  return useMutation({
    mutationFn: (data: { account_id: string; symbol: string; dollar_amount: number }) =>
      paperTradeApi.analyze(data).then((r) => r.data),
  });
}

export function usePaperTradeExecute() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { account_id: string; symbol: string; side: string; dollar_amount: number; price: number }) =>
      paperTradeApi.execute(data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paper-trade-history'] });
      queryClient.invalidateQueries({ queryKey: ['paper-portfolio'] });
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      queryClient.invalidateQueries({ queryKey: ['overview'] });
      queryClient.invalidateQueries({ queryKey: ['portfolio'] });
    },
  });
}

export function usePaperTradeHistory(accountId: string, params?: { page?: number; page_size?: number }) {
  return useQuery({
    queryKey: ['paper-trade-history', accountId, params],
    queryFn: () => paperTradeApi.history(accountId, params).then((r) => r.data),
    enabled: !!accountId,
  });
}

export function usePaperPortfolio(accountId: string) {
  return useQuery({
    queryKey: ['paper-portfolio', accountId],
    queryFn: () => paperTradeApi.portfolio(accountId).then((r) => r.data),
    enabled: !!accountId,
    refetchInterval: 15000,
  });
}

export function usePaperTradeReset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (accountId: string) => paperTradeApi.reset(accountId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paper-trade-history'] });
      queryClient.invalidateQueries({ queryKey: ['paper-portfolio'] });
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}

// --- Market Data ---
export function useMarketPrices(symbols?: string) {
  return useQuery({
    queryKey: ['market-prices', symbols],
    queryFn: () => marketApi.prices(symbols).then((r) => r.data),
    refetchInterval: 15000,
  });
}

export function useMarketTicker(symbol: string) {
  return useQuery({
    queryKey: ['market-ticker', symbol],
    queryFn: () => marketApi.ticker(symbol).then((r) => r.data),
    enabled: !!symbol,
    refetchInterval: 10000,
  });
}

export function useMarketOverview() {
  return useQuery({
    queryKey: ['market-overview'],
    queryFn: () => marketApi.overview().then((r) => r.data),
    refetchInterval: 60000,
  });
}

export function useMarketCandles(symbol: string, days?: number) {
  return useQuery({
    queryKey: ['market-candles', symbol, days],
    queryFn: () => marketApi.candles(symbol, days).then((r) => r.data),
    enabled: !!symbol,
  });
}

// --- Crypto.com ---
export function useCryptoComStatus() {
  return useQuery({
    queryKey: ['cryptocom-status'],
    queryFn: () => cryptocomApi.status().then((r) => r.data),
    refetchInterval: 30000,
  });
}

export function useCryptoComBalances(scope?: string) {
  return useQuery({
    queryKey: ['cryptocom-balances', scope],
    queryFn: () => cryptocomApi.balances(scope).then((r) => r.data),
    refetchInterval: 30000,
  });
}

export function useCryptoComTradingLimit() {
  return useQuery({
    queryKey: ['cryptocom-trading-limit'],
    queryFn: () => cryptocomApi.tradingLimit().then((r) => r.data),
  });
}

export function useCryptoComQuote() {
  return useMutation({
    mutationFn: cryptocomApi.quote,
  });
}

export function useCryptoComConfirm() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: cryptocomApi.confirm,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cryptocom-balances'] });
      queryClient.invalidateQueries({ queryKey: ['cryptocom-status'] });
    },
  });
}

export function useCryptoComHistory() {
  return useQuery({
    queryKey: ['cryptocom-history'],
    queryFn: () => cryptocomApi.history().then((r) => r.data),
  });
}

export function useCryptoComCoinSearch(query: string) {
  return useQuery({
    queryKey: ['cryptocom-coin-search', query],
    queryFn: () => cryptocomApi.coinSearch(query).then((r) => r.data),
    enabled: !!query && query.length >= 2,
  });
}

// --- Risk Engine ---
export function useRiskStatus() {
  return useQuery({
    queryKey: ['risk-status'],
    queryFn: () => riskApi.riskStatus().then((r) => r.data),
    refetchInterval: 15000,
  });
}

export function useRiskShutdownStatus() {
  return useQuery({
    queryKey: ['risk-shutdown-status'],
    queryFn: () => riskApi.shutdownStatus().then((r) => r.data),
    refetchInterval: 10000,
  });
}

export function useRiskActivateShutdown() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) => riskApi.activateShutdown(reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['risk-shutdown-status'] });
      queryClient.invalidateQueries({ queryKey: ['risk-status'] });
    },
  });
}

export function useRiskDeactivateShutdown() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (confirm: boolean) => riskApi.deactivateShutdown(confirm),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['risk-shutdown-status'] });
      queryClient.invalidateQueries({ queryKey: ['risk-status'] });
    },
  });
}

export function useRiskEvents(params?: { account_id?: string; severity?: string; page?: number; page_size?: number }) {
  return useQuery({
    queryKey: ['risk-events', params],
    queryFn: () => riskApi.events(params).then((r) => r.data),
    refetchInterval: 30000,
  });
}

export function useAccountRiskConfig(accountId: string) {
  return useQuery({
    queryKey: ['risk-config', accountId],
    queryFn: () => riskApi.accountConfig(accountId).then((r) => r.data),
    enabled: !!accountId,
  });
}
